import { useContext } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { getSvocId, getUserId } from '../utils/utils';
import type { QueryError } from '../api/authError';
import type { MilitaryDiscountDetails } from '../api/types';
import type { ExperienceContextValue } from '../types/experience-context';

interface MilitaryDiscountDetailsResult {
  details: MilitaryDiscountDetails | null;
  loading: boolean;
  error: QueryError | undefined;
  hasIdentifiers: boolean;
  refetch: () => Promise<unknown>;
}

/* Enrollment as the BFF holds it, for the one page that asks.
/*
/* Reads its own identifiers and channel rather than taking them as arguments,
/* the way requestCreator did for the REST call this replaces. The guard that
/* decides whether there is a customer to ask about and the variables the query
/* is sent with have to come from the same place, or a caller could skip on one
/* answer and query on the other. */
export const useMilitaryDiscountDetails = (): MilitaryDiscountDetailsResult => {
  const { channel } = useContext<ExperienceContextValue>(ExperienceContext);
  const userId = getUserId();
  const customerAccountId = getSvocId();
  /* The same mapping the REST call's `channelId` header carried. */
  const channelId = channel === 'mobile' ? 2 : 1;
  const hasIdentifiers = !!userId && !!customerAccountId;

  const { data, loading, error, refetch } = useDataModel(
    'militaryDiscountDetails',
    {
      variables: { userId, customerAccountId, channelId },
      /* Identity comes from browser globals and the query is asked against the
      /* customer's own session, so there is nothing to ask on the server - which
      /* renders an empty container anyway, see src/server.jsx. */
      ssr: false,
      context: { withAuth: true },
      /* Enrollment is what this page is about, so it is asked afresh every time
      /* the page loads rather than served out of a cache that outlived the
      /* answer - the REST call it replaces went to the network unconditionally.
      /* Cached data still renders immediately while that happens. */
      fetchPolicy: 'cache-and-network',
      /* A visitor with no session has nothing to ask about, and asking anyway
      /* would send null identifiers. Left for the page to act on: it hands them
      /* to sign in, carrying an in-flight ID.me verification with them. */
      skip: !hasIdentifiers,
    },
  );

  return {
    details: data?.militaryDiscountDetails ?? null,
    loading,
    error,
    hasIdentifiers,
    refetch,
  };
};
